import { useCallback, useRef, useState } from 'react';
import { callDetectingLostPromise } from 'shared/utils/request-tracker';

type AsyncFunction<T = any> = (...args: any[]) => Promise<T>;
type SyncFunction = (...args: any[]) => void;

interface UseAsyncButtonResult<T = any> {
  isLoading: boolean;
  handleClick: (...args: any[]) => Promise<T | void>;
  error: Error | null;
}

const isPromise = <T>(value: unknown): value is Promise<T> => typeof (value as Promise<T> | undefined)?.then === 'function';

/**
 * Подпись нажатой кнопки из события клика — для dev-детектора `[lost-promise]`.
 *
 * Пропсы тут не помогают: подпись почти всегда `<Trans>`, а не строка, и детектор писал бы безликое «UDButton».
 * В DOM к моменту клика уже переведённый текст: `aria-label`, иначе `textContent`. Не событие или кнопка без
 * текста (одна иконка) — `undefined`, и вызывающий берёт свою запасную подпись.
 */
export const labelFromClickEvent = (event: unknown): string | undefined => {
  const target = (event as { currentTarget?: unknown } | null | undefined)?.currentTarget;
  if (!(target instanceof Element)) return undefined;
  return target.getAttribute('aria-label')?.trim() || target.textContent?.trim() || undefined;
};

export const useAsyncButton = <T = any>(
  handler?: AsyncFunction<T> | SyncFunction,
  options: {
    onError?: (error: Error) => void;
    onSuccess?: (result: T) => void;
    /**
     * Подпись кнопки для dev-детектора «потерянного промиса» (`shared/utils/request-tracker.ts`). Получает те же
     * аргументы, что обработчик — обычно событие клика (см. `labelFromClickEvent`); `() => string` тоже годится.
     */
    label?: (...clickArgs: any[]) => string;
  } = {},
): UseAsyncButtonResult<T> => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Замок — ref, а не состояние: второй клик может прийти раньше перерисовки, и `isLoading`
  // из замыкания его бы не увидел (IMGM-1561). Состояние нужно только для отрисовки спиннера.
  const lockRef = useRef(false);

  const handleClick = useCallback(
    async (...args: any[]) => {
      if (!handler || lockRef.current) return;

      const succeed = (result: T | void) => {
        if (result as T) {
          options.onSuccess?.(result as T);
        }
        return result;
      };

      const fail = (err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        if (!options.onError) {
          throw error;
        }
        options.onError(error);
      };

      setError(null);

      let result: T | Promise<T> | void;
      try {
        result = callDetectingLostPromise(
          () => handler(...args),
          () => options.label?.(...args) ?? 'кнопка',
        );
      } catch (err) {
        return fail(err);
      }

      // Синхронный обработчик в loading не переводим. Иначе кнопка перерисовывалась бы прямо
      // посреди обработки клика (иконка заменяется спиннером) и элемент, по которому кликнули,
      // отрывался бы от DOM — а логика «клик снаружи» (useCloseOnOutsideOrEsc и подобная)
      // читает DOM уже после этого и принимает такой клик за внешний.
      if (!isPromise<T>(result)) {
        return succeed(result);
      }

      lockRef.current = true;
      setIsLoading(true);
      try {
        return succeed(await result);
      } catch (err) {
        return fail(err);
      } finally {
        // И после ошибки: застрявшая кнопка — тоже баг.
        lockRef.current = false;
        setIsLoading(false);
      }
    },
    [handler, options],
  );

  return {
    isLoading,
    handleClick,
    error,
  };
};
